import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import axios from 'axios';
import { AlertTriangle, WifiOff } from 'lucide-react';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api'
  : '/api';

const AdvancedStockChart = ({ symbol = 'AAPL', height = 500, onPriceUpdate, user, category, asset, currency, usdTryRate = 42.5 }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const onPriceUpdateRef = useRef(onPriceUpdate);
  const [timeframe, setTimeframe] = useState('15m');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState('ok');

  const currentPrice = asset?.current_price || 0;
  const changePercent = asset?.change_percent || 0;

  onPriceUpdateRef.current = onPriceUpdate;

  const fetchChartData = useCallback(async (tf) => {
    if (!symbol) return [];
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/chart-data/${symbol}?interval=${tf}`, { timeout: 10000 });
      if (response.data?.status === 'success' && response.data?.data?.length > 0) {
        setApiStatus('ok');
        return response.data.data.map(c => ({
          time: c.timestamp,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close
        }));
      }
      setApiStatus('warning');
      setError('Grafik verisi bos dondu');
      return [];
    } catch (err) {
      console.error('Chart data fetch error:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setApiStatus('error');
        setError('API baglantisi zaman asimina ugradi');
      } else if (err.response) {
        setApiStatus('error');
        setError(`API Hatasi: ${err.response.status} - Sunucu hatasi`);
      } else if (err.request) {
        setApiStatus('error');
        setError('API baglantisi kurulamadi - Sunucu erisilemiyor');
      } else {
        setApiStatus('warning');
        setError('Grafik verisi yuklenemedi');
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    let cancelled = false;

    const initChart = async () => {
      const candles = await fetchChartData(timeframe);
      if (cancelled || !chartContainerRef.current) return;

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
      }

      if (candles.length === 0) return;

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: height - 80,
        layout: {
          background: { color: '#131722' },
          textColor: '#D9D9D9',
          attributionLogo: false,
        },
        grid: {
          vertLines: { color: 'rgba(42, 46, 57, 0.3)' },
          horzLines: { color: 'rgba(42, 46, 57, 0.3)' },
        },
        crosshair: { mode: 1 },
        rightPriceScale: {
          borderColor: '#2a2e39',
          scaleMargins: { top: 0.1, bottom: 0.1 }
        },
        timeScale: {
          borderColor: '#2a2e39',
          timeVisible: true,
          secondsVisible: false,
        },
        localization: { locale: 'tr-TR' },
        handleScroll: { vertTouchDrag: false },
      });

      chartRef.current = chart;

      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
      });

      candleSeriesRef.current = candleSeries;
      candleSeries.setData(candles);
      chart.timeScale().fitContent();

      const handleResize = () => {
        if (chartRef.current && chartContainerRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    };

    initChart();

    return () => {
      cancelled = true;
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
      }
    };
  }, [symbol, timeframe, height, fetchChartData]);

  useEffect(() => {
    if (!candleSeriesRef.current || !currentPrice) return;

    const now = Math.floor(Date.now() / 1000);
    const intervals = { '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600 };
    const intervalSeconds = intervals[timeframe] || 900;
    const candleTime = Math.floor(now / intervalSeconds) * intervalSeconds;

    try {
      candleSeriesRef.current.update({
        time: candleTime,
        open: currentPrice,
        high: currentPrice * 1.001,
        low: currentPrice * 0.999,
        close: currentPrice
      });
    } catch (e) {
      // Chart not ready
    }
  }, [currentPrice, timeframe]);

  useEffect(() => {
    if (onPriceUpdateRef.current && currentPrice > 0) {
      onPriceUpdateRef.current(currentPrice, changePercent, currency === 'USD' ? currentPrice : null);
    }
  }, [currentPrice, changePercent, currency]);

  const timeframes = [
    { label: '1D', value: '1m' },
    { label: '5D', value: '5m' },
    { label: '15D', value: '15m' },
    { label: '30D', value: '30m' },
    { label: '1S', value: '1h' },
  ];

  const currencySymbol = currency === 'USD' ? '$' : '\u20BA';
  const isPositive = changePercent >= 0;

  return (
    <div className="bg-gray-900 rounded-lg overflow-hidden" style={{ touchAction: 'pan-y' }}>
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <span className="text-white font-bold text-lg">{symbol}</span>
          <span className={`text-2xl font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {currencySymbol}{currentPrice?.toFixed(2) || '0.00'}
          </span>
          <span className={`text-sm px-2 py-1 rounded ${isPositive ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
            {isPositive ? '+' : ''}{changePercent?.toFixed(2) || '0.00'}%
          </span>
        </div>

        {/* Timeframe buttons */}
        <div className="flex gap-1">
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                timeframe === tf.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ position: 'relative' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 z-10">
            <div className="text-gray-400 text-sm animate-pulse">Grafik yukleniyor...</div>
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 flex items-center justify-center z-20" style={{ pointerEvents: 'none' }}>
            <div data-testid="chart-api-error" className="flex flex-col items-center gap-3 px-6 py-5 rounded-xl border max-w-sm mx-4"
              style={{
                background: apiStatus === 'error' 
                  ? 'rgba(127, 29, 29, 0.85)' 
                  : 'rgba(120, 53, 15, 0.85)',
                borderColor: apiStatus === 'error' 
                  ? 'rgba(239, 68, 68, 0.5)' 
                  : 'rgba(245, 158, 11, 0.5)',
                backdropFilter: 'blur(8px)',
                pointerEvents: 'auto'
              }}
            >
              <div className="flex items-center gap-2">
                {apiStatus === 'error' ? (
                  <WifiOff className="w-5 h-5 text-red-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                )}
                <span className={`font-bold text-sm ${apiStatus === 'error' ? 'text-red-300' : 'text-yellow-300'}`}>
                  API Baglanti Hatasi
                </span>
              </div>
              <p className="text-xs text-gray-200 text-center leading-relaxed">
                {error}
              </p>
              <button 
                onClick={() => { setError(null); setApiStatus('ok'); window.location.reload(); }}
                className={`text-xs px-4 py-1.5 rounded-lg font-medium transition-colors ${
                  apiStatus === 'error' 
                    ? 'bg-red-600 hover:bg-red-500 text-white' 
                    : 'bg-yellow-600 hover:bg-yellow-500 text-white'
                }`}
              >
                Tekrar Dene
              </button>
            </div>
          </div>
        )}
        <div ref={chartContainerRef} className="w-full" />
      </div>

      {/* Footer */}
      <div className="bg-gray-800 px-4 py-2 flex justify-between items-center text-xs text-gray-400">
        <div className="flex gap-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded"></span> Yukselis
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded"></span> Dusus
          </span>
        </div>
        <span>{symbol} - Anlik</span>
      </div>
    </div>
  );
};

export default AdvancedStockChart;
